import { ClipboardList, FileClock, Loader2, Mic, Sparkles } from "lucide-react";
import type { JSX } from "react";
import { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { V3Button } from "@/components/ui/v3/button";
import { V3Drawer } from "@/components/ui/v3/drawer";
import { useRecorderNoteSettings } from "@/hooks/visits/useRecorderNoteSettings";
import { createPreChart, preChartBody } from "@/lib/pre-chart";
import { cn } from "@/lib/utils";
import { useAuthStore } from "@/store/auth.store";
import { RecorderTemplateChip } from "./RecorderTemplateChip";

// The ink "Pre-chart" button beside New visit: outline, so New visit stays
// the main action.
export const PRE_CHART_BUTTON =
  "inline-flex h-11 shrink-0 items-center justify-center gap-1.5 rounded-full bg-v3-panel px-4 font-v3 text-[14px] font-semibold text-v3-text shadow-v3-hairline-strong transition-colors hover:bg-v3-surface-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-v3-brand active:scale-[0.98] lg:h-9 [&_svg]:size-4 [&_svg]:shrink-0";

export const PreChartButton = ({
  onClick,
  className,
}: {
  onClick: () => void;
  className?: string;
}): JSX.Element => {
  const { t } = useTranslation();
  return (
    <button
      className={cn(PRE_CHART_BUTTON, className)}
      data-testid="pre-chart-button"
      type="button"
      onClick={onClick}
    >
      <ClipboardList aria-hidden />
      {t("preChart.button")}
    </button>
  );
};

const STEPS = [
  { icon: Sparkles, key: "fill" },
  { icon: FileClock, key: "draft" },
  { icon: Mic, key: "start" },
] as const;

/**
 * Pre-chart (VITE_PRE_CHART_ENABLED): pick the template and an optional
 * name, then the draft opens in the visit workspace, where the assistant
 * fills it (typing, dictation, uploads). Nothing is recorded or charged.
 */
export const PreChartSheet = ({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (visitPublicId: string) => void;
}): JSX.Element => {
  const { t } = useTranslation();
  const titleId = useId();
  const settings = useRecorderNoteSettings(undefined, open);
  const uid = useAuthStore((state) => state.user?.uid);
  const [title, setTitle] = useState("");
  const [creating, setCreating] = useState(false);
  const [failed, setFailed] = useState(false);
  // One key per pre-chart: a retry of the same input reuses it, so a lost
  // response never creates two drafts; changed input gets a new key.
  const attempt = useRef<{ key: string; body: string } | null>(null);

  useEffect(() => {
    if (!open) return;
    setTitle("");
    setFailed(false);
    setCreating(false);
    attempt.current = null;
  }, [open]);

  const values = settings.values;
  const create = async (): Promise<void> => {
    if (!values || !uid || creating) return;
    const body = preChartBody(values, title);
    const serialized = JSON.stringify(body);
    if (attempt.current?.body !== serialized)
      attempt.current = { key: crypto.randomUUID(), body: serialized };
    setCreating(true);
    setFailed(false);
    try {
      const id = await createPreChart(body, {
        key: attempt.current.key,
        userUid: uid,
      });
      onCreated(id);
      onOpenChange(false);
    } catch {
      setFailed(true);
    } finally {
      setCreating(false);
    }
  };

  return (
    <V3Drawer
      footer={
        <div className="space-y-2">
          {failed && (
            <p
              className="rounded-xl bg-v3-rec-tint px-4 py-3 text-[14px] leading-5 text-v3-rec-text"
              data-testid="pre-chart-error"
              role="alert"
            >
              {t("preChart.sheet.error")}
            </p>
          )}
          <V3Button
            className="w-full"
            data-testid="pre-chart-create"
            disabled={!values || !uid || creating}
            size="lg"
            variant="primary"
            onClick={() => {
              void create();
            }}
          >
            {creating ? (
              <Loader2 aria-hidden className="animate-spin" />
            ) : (
              <ClipboardList aria-hidden />
            )}
            {t("preChart.sheet.create")}
          </V3Button>
        </div>
      }
      open={open}
      testId="pre-chart-sheet"
      title={t("preChart.sheet.title")}
      onOpenChange={(next) => {
        if (!creating) onOpenChange(next);
      }}
    >
      <div className="space-y-6 px-5 pb-4 pt-1">
        <div>
          <h2 className="font-v3-display text-[24px] font-bold leading-8 tracking-[-0.01em]">
            {t("preChart.sheet.heading")}
          </h2>
          <p className="mt-1.5 text-[15px] leading-[22px] text-v3-text-2">
            {t("preChart.sheet.description")}
          </p>
        </div>
        <ol className="space-y-2">
          {STEPS.map(({ icon: Icon, key }, index) => (
            <li
              key={key}
              className="flex items-start gap-3 rounded-xl bg-v3-surface-2 px-3.5 py-3"
            >
              <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-v3-panel shadow-v3-hairline">
                <Icon aria-hidden className="size-4 text-v3-text-2" />
              </span>
              <span className="min-w-0 text-[14px] leading-5">
                <span className="block font-semibold text-v3-text">
                  {index + 1}. {t(`preChart.sheet.steps.${key}.title`)}
                </span>
                <span className="block text-v3-text-3">
                  {t(`preChart.sheet.steps.${key}.body`)}
                </span>
              </span>
            </li>
          ))}
        </ol>
        <div className="space-y-2">
          <p className="text-[13px] font-semibold text-v3-text-2">
            {t("preChart.sheet.template")}
          </p>
          <RecorderTemplateChip settings={settings} />
        </div>
        <div className="space-y-2">
          <label
            className="block text-[13px] font-semibold text-v3-text-2"
            htmlFor={titleId}
          >
            {t("preChart.sheet.name")}
          </label>
          <input
            className="h-11 w-full rounded-xl bg-v3-panel px-3.5 text-[15px] text-v3-text shadow-v3-hairline-strong placeholder:text-v3-text-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-v3-brand-ring"
            data-testid="pre-chart-name"
            id={titleId}
            maxLength={128}
            placeholder={t("preChart.sheet.namePlaceholder")}
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") void create();
            }}
          />
          <p className="text-[12.5px] leading-[18px] text-v3-text-3">
            {t("preChart.sheet.nameHint")}
          </p>
        </div>
      </div>
    </V3Drawer>
  );
};
